import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { QuoteButton } from "@/components/QuoteButton";
import { TRAITEUR_SUBMENU } from "@/lib/data";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Cheffe à domicile", to: "/cheffe-a-domicile" },
  { label: "Coordination événementielle", to: "/coordination" },
  { label: "Nos réalisations", to: "/realisations" },
];

export function Logo({ dark = false }) {
  return (
    <Link to="/" data-testid="logo-home" className="flex items-center">
      <img
        src="/afroregal-logo.webp"
        alt="Afro Regal — Créateurs de beaux moments"
        className="h-16 w-auto lg:h-20"
      />
    </Link>
  );
}

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [traiteurOpen, setTraiteurOpen] = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const phone = content?.contact?.phone || "06 41 28 33 85";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-200 hover:text-[#C9A84C] ${
      isActive ? "text-[#C9A84C]" : "text-[#2C2C2C]"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-300 ${
        scrolled ? "bg-[#FAF6EF]/95 backdrop-blur-xl shadow-[0_1px_0_rgba(44,44,44,0.08)] py-3" : "bg-[#FAF6EF]/80 backdrop-blur-md py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex">
          <NavLink to="/" className={linkCls} data-testid="nav-accueil" end>
            Accueil
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setTraiteurOpen(true)}
            onMouseLeave={() => setTraiteurOpen(false)}
          >
            <NavLink to="/traiteur" className={linkCls} data-testid="nav-traiteur">
              <span className="inline-flex items-center gap-1">
                Traiteur <ChevronDown size={14} />
              </span>
            </NavLink>
            {traiteurOpen && (
              <div className="absolute left-0 top-full w-64 pt-4">
                <div className="rounded-lg border border-[#2C2C2C]/10 bg-white p-2 shadow-xl">
                  {TRAITEUR_SUBMENU.map((s) => (
                    <Link
                      key={s.label}
                      to={s.to}
                      data-testid={`submenu-${s.label}`}
                      className="block rounded-md px-3 py-2 text-sm text-[#2C2C2C] transition-colors hover:bg-[#FAF6EF] hover:text-[#C9A84C]"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/menus" className={linkCls} data-testid="nav-carte">
            La carte
          </NavLink>
          <NavLink to="/cheffe-a-domicile" className={linkCls} data-testid="nav-cheffe">
            Cheffe à domicile
          </NavLink>
          <NavLink to="/coordination" className={linkCls} data-testid="nav-coordination">
            Coordination
          </NavLink>
          <NavLink to="/realisations" className={linkCls} data-testid="nav-realisations">
            Nos réalisations
          </NavLink>
        </nav>

        <div className="hidden items-center gap-4 lg:ml-4 lg:flex">
          <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} data-testid="header-phone" aria-label={`Nous appeler au ${phone}`} className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-[#2C2C2C] transition-colors hover:text-[#9A7B2C]">
            <Phone size={16} className="text-[#C9A84C]" /> <span className="hidden xl:inline">{phone}</span>
          </a>
          <QuoteButton testId="header-quote-btn" />
        </div>

        <button
          className="lg:hidden text-[#2C2C2C]"
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden" data-testid="mobile-menu" onClick={(e) => { if (e.target.closest("a")) setMobileOpen(false); }}>
          <div className="mx-4 mt-3 rounded-xl border border-[#2C2C2C]/10 bg-white p-5 shadow-xl">
            <nav className="flex flex-col gap-1">
              <NavLink to="/" className="py-2 text-[#2C2C2C]" data-testid="m-nav-accueil" end>Accueil</NavLink>
              <NavLink to="/traiteur" className="py-2 text-[#2C2C2C]" data-testid="m-nav-traiteur">Traiteur</NavLink>
              <div className="pl-4">
                {TRAITEUR_SUBMENU.map((s) => (
                  <Link key={s.label} to={s.to} className="block py-1.5 text-sm text-[#2C2C2C]/70">{s.label}</Link>
                ))}
              </div>
              <NavLink to="/menus" className="py-2 text-[#2C2C2C]" data-testid="m-nav-carte">La carte</NavLink>
              <NavLink to="/cheffe-a-domicile" className="py-2 text-[#2C2C2C]" data-testid="m-nav-cheffe">Cheffe à domicile</NavLink>
              <NavLink to="/coordination" className="py-2 text-[#2C2C2C]" data-testid="m-nav-coordination">Coordination événementielle</NavLink>
              <NavLink to="/realisations" className="py-2 text-[#2C2C2C]" data-testid="m-nav-realisations">Nos réalisations</NavLink>
            </nav>
            <div className="mt-4">
              <QuoteButton testId="mobile-quote-btn" className="w-full" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
